import classNames from 'classnames';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export enum TooltipPositionEnum {
  Top = 'top',
  Bottom = 'bottom',
  Left = 'left',
  Right = 'right',
}

export interface ITooltip {
  readonly isActive: boolean;
  readonly position: TooltipPositionEnum;
  readonly overflowLeft: boolean;
  readonly overflowRight: boolean;
  readonly buttonEl: ReactNode;
  readonly background?: 'white' | 'grey';
  readonly offset?: number;
  readonly wrapperStyles?: string;
}

const Tooltip = ({
  isActive,
  overflowLeft,
  overflowRight,
  position = TooltipPositionEnum.Bottom,
  buttonEl,
  background = 'white',
  children,
  offset = 24,
  wrapperStyles,
}: PropsWithChildren<ITooltip>) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipOffset, setTooltipOffset] = useState({ x: offset, y: offset });
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    if (isActive && tooltipRef.current)
      setTooltipOffset({
        y: tooltipRef.current.offsetHeight + offset,
        x: tooltipRef.current.offsetWidth + offset,
      });
  }, [isActive, children]);

  const tooltipStyles = useMemo(() => {
    if (
      position === TooltipPositionEnum.Top ||
      position === TooltipPositionEnum.Bottom
    ) {
      return {
        bottom:
          position === TooltipPositionEnum.Bottom
            ? `-${tooltipOffset.y}px`
            : undefined,
        top:
          position === TooltipPositionEnum.Top
            ? `-${tooltipOffset.y}px`
            : undefined,
        right: overflowRight ? `-25%` : undefined,
        left: overflowLeft ? `-25%` : undefined,
      };
    } else {
      return {
        right:
          position === TooltipPositionEnum.Right
            ? `-${tooltipOffset.x}px`
            : undefined,
        left:
          position === TooltipPositionEnum.Left
            ? `-${tooltipOffset.x}px`
            : undefined,
      };
    }
  }, [position, overflowLeft, overflowRight, tooltipOffset]);

  const tooltipClassNames = classNames(
    'tw-absolute tw-min-w-max tw-rounded',
    background === 'grey' ? 'tw-bg-grey-20 tooltip-grey' : 'tw-bg-white',
    {
      'tooltip-bottom': position === TooltipPositionEnum.Bottom,
      'tooltip-top': position === TooltipPositionEnum.Top,
      'tooltip-left': position === TooltipPositionEnum.Left,
      'tooltip-right': position === TooltipPositionEnum.Right,
      'tooltip-overflow-right': overflowRight,
      'tooltip-overflow-left': overflowLeft,
    }
  );

  const defaultState = {
    opacity: 0,
    y:
      (position === TooltipPositionEnum.Bottom && -10) ||
      (position === TooltipPositionEnum.Top && 10) ||
      undefined,
    x:
      (position === TooltipPositionEnum.Left && -10) ||
      (position === TooltipPositionEnum.Right && 10) ||
      undefined,
  };

  return (
    <div
      className={classNames(
        'tw-relative tw-z-10 tw-flex tw-flex-col tw-items-center tw-justify-center',
        wrapperStyles
      )}
    >
      {buttonEl}
      <AnimatePresence mode="wait">
        {isActive && (
          <motion.div
            ref={tooltipRef}
            style={tooltipStyles}
            className={classNames('tooltip-container', tooltipClassNames)}
            initial={defaultState}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={defaultState}
            transition={{ duration: shouldReduceMotion ? 0 : 0.3 }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Tooltip;
